import { store } from '@redux/store';
import { fromEvent, map, distinctUntilChanged, combineLatest, shareReplay, take } from 'rxjs';
import ModalStep from '../enums/modalStep';
import { setModalStep } from '../redux/slices';
import Email from './email';
import Phone from './phone';
import UserStatus from '../enums/userStatus';
import { getVerificationMethodStep } from '../services/verificationMethodService';

export default class SignUp {
  constructor(rootComponent) {
    this.subscriptions = [];
    this.rootComponent = rootComponent;
    this.panelSelector = '[data-sign-up-panel]';
    this.previousStep = ModalStep.TermsAndConditions;
    this.email = new Email();
    this.phone = new Phone();
    this.phone.placeholder = '(_ _ _) _ _ _-_ _ _ _';
    this.rootComponent.subscribe('domUpdate', this.onDomUpdate.bind(this));
  }

  /**
   * Handles the success response of the email verification process.
   *
   * @param {Object} data - The response data from the email verification process.
   * @param {Object} data.status - The status object containing the verification status.
   * @param {string} data.status.key - The key representing the user's verification status.
   *
   */
  onVerifyEmailSuccess(data) {
    this.rootComponent.setPrimaryButtonDisabled(false);
    this.rootComponent.removeNotification();
    const { status, error } = data;

    if (error) {
      this.showErrorMessage('An error has occurred. Please try again.');
      return;
    }

    switch (UserStatus.fromKey(status.name)) {
      // Both Verified and NotVerified statuses indicate that the email is already associated with an account.
      // We show an error message and reset the inputs to prompt the user to try again or use a different email.
      case UserStatus.Verified:
      case UserStatus.NotVerified: {
        this.showErrorMessage('A UTour account already exists for this email.');
        this.resetInputs();
        break;
      }
      case UserStatus.NotFound: {
        const step = getVerificationMethodStep(this.rootComponent.verificationMethods);
        store.dispatch(setModalStep({ modalStep: step }));
        break;
      }
      case UserStatus.UserBlocked:
        this.showErrorMessage('User is blocked. Please contact support.');
        break;
      default:
        this.rootComponent.setPrimaryButtonDisabled(false);
        this.showErrorMessage('An error has occurred. Please try again.');
        break;
    }
  }

  /**
   * Resets the input fields for email and phone by clearing their values.
   * This method is typically used to reset the form to its initial state.
   */
  resetInputs() {
    this.email.reset();
    this.phone.reset();
  }

  /**
   * Handles the primary action by dispatching an action to update the modal step
   * in the application state.
   */
  handlePrimaryAction() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    const emailValue = this.email.getValue();
    this.rootComponent.setPrimaryButtonDisabled(true);
    const onVerifyEmail$ = this.rootComponent.verifyUserEmail$(emailValue).pipe(take(1), shareReplay(1));
    this.subscriptions.push(onVerifyEmail$.subscribe(this.setUserData.bind(this)));
    this.subscriptions.push(onVerifyEmail$.subscribe(this.onVerifyEmailSuccess.bind(this)));
  }

  /**
   * Determines whether the primary button should be enabled.
   *
   * @returns {boolean} - Returns `true` if both the email and phone fields have valid values; otherwise, `false`.
   */
  shouldEnablePrimaryButton() {
    return this.email.getValue() && this.email.validate() && this.phone.getValue() && this.phone.validate();
  }

  /**
   * Handles the secondary action by dispatching an action to update the modal step
   * to the previous step in the application state.
   */
  handleSecondaryAction() {
    store.dispatch(setModalStep({ modalStep: this.previousStep }));
  }

  /**
   * Binds event listeners for the components.
   */
  bindEvents() {
    const signInLink = this.getSignInLink();
    const onSignInLinkClick$ = fromEvent(signInLink, 'click');
    onSignInLinkClick$.subscribe(SignUp.moveToSignInPanel);
    this.phone.bindEvents();
  }

  /**
   * Retrieves the sign-in link element from the sign-up panel.
   *
   * @returns {Element | null} The DOM element representing the sign-in link, or null if not found.
   */
  getSignInLink() {
    return this.getSignUpPanel().querySelector('[data-sign-in-link]');
  }

  /**
   * Retrieves the sign-up panel element from the DOM using the specified panel selector.
   *
   * @returns {Element|null} The DOM element matching the panel selector, or null if no match is found.
   */
  getSignUpPanel() {
    return document.querySelector(this.panelSelector);
  }

  /**
   * Handles the DOM update event for the sign-up component.
   *
   * This method is triggered when the DOM is updated and performs the following:
   *
   * @param {CustomEvent} event - The DOM update event containing details about the current modal step.
   */
  onDomUpdate(event) {
    if (event.detail && event.detail.step !== ModalStep.SignUp) {
      this.phone.destroy();
      return;
    }
    const onEmailValidationChange$ = this.email
      .getOnChangeEvent$()
      .pipe(map(this.isEmailValid.bind(this)), distinctUntilChanged());
    const onPhoneValidationChange$ = this.phone
      .getOnChangeEvent$()
      .pipe(map(this.isPhoneValid.bind(this)), distinctUntilChanged());
    this.rootComponent.setPrimaryButtonDisabled(!this.shouldEnablePrimaryButton());
    this.rootComponent.setSecondaryButtonDisabled(false);
    this.bindEvents();

    const checkValidation$ = combineLatest([onEmailValidationChange$, onPhoneValidationChange$]).pipe(
      map(([isEmailValid, isPhoneValid]) => isEmailValid && isPhoneValid)
    );
    checkValidation$.subscribe((isValid) => this.rootComponent.setPrimaryButtonDisabled(!isValid));
  }

  /**
   * Validates the email address associated with the current instance.
   *
   * @returns {boolean} Returns `true` if the email is valid, otherwise `false`.
   */
  isEmailValid() {
    return this.email.validate();
  }

  /**
   * Validates the phone number by invoking the `validate` method on the `phone` object.
   *
   * @returns {boolean} Returns `true` if the phone number is valid, otherwise `false`.
   */
  isPhoneValid() {
    return this.phone.validate();
  }

  /**
   * Updates the user data by notifying changes in user status and user information.
   *
   * @param {Object} param - The parameter object.
   * @param {string} [param.status] - The new status of the user. If provided, it triggers a status change event.
   * @param {Object} [param.user] - The user object containing updated user information.
   * If provided, it triggers a user change event.
   */
  setUserData({ status, user }) {
    this.rootComponent.onUserStatusChange$.next(status);

    if (user) {
      this.rootComponent.onUserChange$.next(user);
      return;
    }

    this.rootComponent.onUserChange$.next({
      email: this.email.getValue(),
      phoneNumber: this.phone.getValue(),
    });
  }

  /**
   * Displays an error message by invoking the notification system of the root component.
   *
   * @param {string} message - The error message to be displayed.
   */
  showErrorMessage(message) {
    this.rootComponent.showNotification(message);
  }

  /**
   * Renders the HTML structure for the sign-up panel of the self-guided tour feature.
   *
   * @returns {string} The HTML string representing the sign-up panel.
   */
  render() {
    this.email.value = '';
    this.phone.value = '';
    return `
      <div class="nhs-c-self-guided-left-panel ${this.class}" data-sign-up-panel>
        <div>
          <h1 class="nhs-c-heading--self-guided-tour nhs-mq-heading--self-guided-tour-large">Sign up to UTour</h1>

          <div class="nhs-c-notification__placeholder" data-self-guided-tour-notifications></div>

          <p class="nhs-c-label--black-900-body-1-medium-regular">Create an account to verify your identity and continue scheduling your tour.</p>
        </div>

        <div class="nhs-o-flex-column-items-start-self-stretch nhs-u-gap--600">
          ${this.email.getHtml()}

          ${this.phone.getHtml()}

          <p class="nhs-c-label--black-900-body-1-medium-regular">Already have an account?
            <span data-sign-in-link class="nhs-c-label--black-1000-body-medium-regular-underline nhs-u-cursor--pointer"> Sign in</span>
          </p>

          <p class="nhs-c-label--black-800-sans-label-1-extra-extra-extra-small-book">By clicking Sign up, I agree to 
            <a href="https://www.utourhomes.com/privacy-terms" target="_blank" class="nhs-u-text-underline nhs-u-color--black-1000 nhs-u-cursor--pointer">
              UTour's Privacy & Terms.
            </a>
          </p>
        </div>
      </div>
    `;
  }

  /**
   * Navigates to the sign-in panel by dispatching an action to update the modal step.
   * This method uses the `store.dispatch` function to trigger the `setModalStep` action
   * with the next step value.
   */
  static moveToSignInPanel() {
    store.dispatch(setModalStep({ modalStep: ModalStep.SignIn }));
  }
}
